import { useLocation, useParams } from "react-router-dom";
import { OfferModel, OfferStateLog } from "../models/OfferModel";
import { useEffect, useState } from "react";
import { formatPrice } from "../utils/currencyUtils";
import EditOfferComp from "../components/EditOfferComp";
import SendMessageComp from "../components/SendMessageComp";
import ViewOfferItemComp from "../components/ViewOfferItemComp";
import offerService from "../services/offerService";
import { formatDate } from "../utils/dateUtils";

const ViewOfferPage = () => {
    const { offerId } = useParams();
    const itemImageURL = process.env.REACT_APP_ITEM_IMAGE_URL;
    const location = useLocation();
    const initialOffer = location.state?.offer ?? null;
    const [offer, setOffer] = useState<OfferModel | null>(initialOffer);
    const [offerItemModalVisible, setOfferItemModalVisible] = useState(false);

    const [offerModalVisible, setOfferModalVisible] = useState(false);
    const [sendMessageModalVisible, setSendMessageModalVisible] = useState(false);


    useEffect(() => {
        if (offerId) {
            getOffer(parseInt(offerId));

        }
    }, [offerId]);

    const getOffer = async (offerId: number) => {
        try {
            const fetchedOffer = await offerService.getOffer(offerId);

            // Update only stateLog in existing offer
            setOffer(prevOffer => {
                if (!prevOffer) return new OfferModel(fetchedOffer); // fallback if null
                return {
                    ...prevOffer,
                    stateLogs: Array.isArray(fetchedOffer.stateLogs)
                        ? fetchedOffer.stateLogs.map(
                            (x: any) => new OfferStateLog(x.offerStateLogId, x.offerId, x.offerState, x.logTime)
                        )
                        : [],
                };
            });
        } catch (error) {
            console.error("Failed to fetch offer:", error);
        }
    }

    return (
        <div className="mt-3 mb-5">
            {!offer && (<div className="text-center gap-2 mt-3 text-secondary">
                <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                <span>  Loading...</span>
            </div>)}
            {offer && (
                <>
                    <div className="card rounded-0">
                        <div className="card-body p-4">
                            <h5 className="card-title mb-4">Offer #{offer.offerId}</h5>

                            <div className='d-flex align-items-center border mb-4'>
                                <img
                                    src={offer.itemImage ? `${itemImageURL}/${offer.itemImage}` : "/images/fallback.png"}
                                    alt={offer.itemName}
                                    className="img-fluid rounded-start border-0 border-end"
                                    style={{ height: '100px', width: '100px', objectFit: 'contain' }}
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.onerror = null;
                                        target.src = "/images/fallback.png";
                                    }}

                                />
                                <div className='d-flex flex-column g-3 mx-3 flex-grow-1'>
                                    <div className="fw-bold small mt-1 text-truncate one-line-text-ellipsis">
                                        {offer.itemName}
                                    </div>
                                    <div className='text-muted small mt-2'>{formatPrice(offer.itemPrice)}</div>


                                </div>
                                <button type="button" className="btn btn-sm btn-outline-dark rounded-0 me-2"
                                    style={{ minWidth: '80px' }} onClick={() => setOfferItemModalVisible(true)} >View</button>

                            </div>
                            <ViewOfferItemComp show={offerItemModalVisible} offerItemId={offer.offerItemId}
                                onClose={() => setOfferItemModalVisible(false)} />
                            <div className="row mt-3">
                                <div className="col-4">
                                    <label className="text-label">Qty</label>
                                    <p className="text-value mb-2">{offer!.quantity}</p>
                                </div>
                                <div className="col-4">
                                    <label className="text-label">Discount</label>
                                    <p className="text-value mb-2">{formatPrice(offer.discount)}</p>
                                </div>
                                <div className="col-4">
                                    <label className="text-label">Amount</label>
                                    <p className="text-value mb-2">{formatPrice(offer.itemPrice * offer.quantity - offer.discount)}</p>
                                </div>
                            </div>
                            <label className="text-label mt-3" style={{ whiteSpace: "pre-line" }}>Notes</label>
                            <p className="text-value mb-2 ">{offer.notes || "-"}</p>
                            <label className="text-label mt-3 ">Status</label>
                            <p className="text-value mb-2">{offer.state}</p>
                            {offer.state !== "Completed" && offer.state !== "Canceled" && <button type="button" className="btn btn-outline-dark rounded-0 mt-3 " onClick={() => setOfferModalVisible(true)} >Edit</button>}
                            
                        </div>
                        {offer && (
                            <EditOfferComp show={offerModalVisible} offer={offer}
                                onClose={() => setOfferModalVisible(false)}
                                onOfferUpdated={(updatedOffer: OfferModel) => {
                                    setOffer(updatedOffer);
                                    setOfferModalVisible(false);
                                    getOffer(updatedOffer.offerId);
                                }} />
                        )}
                    </div>
                    <div className="card rounded-0 mt-3">
                        <div className="card-body p-4">
                            <h5 className="card-title mb-4">Buyer</h5>
                            <label className="text-label mt-1">Name</label>
                            <p className="text-value mb-2">{offer ? `${offer.customerFirstname} ${offer.customerLastname}` : '-'}</p>

                            <label className="text-label">Company</label>
                            <p className="text-value mb-2">{offer ? offer.customerCompanyName || '-' : '-'}</p>
                            <label className="text-label">Address</label>
                            <p className="text-value mb-2">{offer ? `${offer.customerAddressLine1} ${offer.customerAddressLine2}`.trim() || '-' : '-'}</p>
                            <div className="row">
                                <div className="col-4">
                                    <label className="text-label">City</label>
                                    <p className="text-value mb-2">{offer ? offer.customerCity || '-' : '-'}</p>
                                </div>
                                <div className="col-4">
                                    <label className="text-label">State</label>
                                    <p className="text-value mb-2">{offer ? offer.state || '-' : '-'}</p>
                                </div>

                            </div>

                            <div className="row">
                                <div className="col-4">
                                    <label className="text-label">Postcode</label>
                                    <p className="text-value mb-2">{offer ? offer.customerPostcode || '-' : '-'}</p>
                                </div>
                                <div className="col-4">
                                    <label className="text-label">Country</label>
                                    <p className="text-value mb-2">{offer ? offer.customerCountry || '-' : '-'}</p>
                                </div>

                            </div>
                            <label className="text-label">Contact Number</label>
                            <p className="text-value mb-2">{offer ? offer.customerContactNumber || '-' : '-'}</p>
                            <button type="button" className="btn btn-outline-dark rounded-0 mt-3 " onClick={() => setSendMessageModalVisible(true)}><i className="bi bi-send me-1"></i>Send Message to Buyer</button>
                        </div>
                        {offer && (
                            <SendMessageComp show={sendMessageModalVisible} offerId={offer.offerId}
                                onClose={() => setSendMessageModalVisible(false)}
                            />
                        )}
                    </div>
                    <div className="card rounded-0 mt-3">
                        <div className="card-body p-4">
                            <h5 className="card-title mb-4">Offer Status Log</h5>
                            {offer && offer.stateLogs && offer.stateLogs.length > 0 &&
                                <ul className="list-group list-group-flush" style={{ maxHeight: '275px', overflowY: 'auto' }} >
                                    {offer.stateLogs
                                        .sort((a, b) => b.offerStateLogId - a.offerStateLogId)
                                        .map((log, index, arr) => (
                                            <li
                                                key={log.offerStateLogId}
                                                className="py-3 px-2 list-group-item d-flex justify-content-between align-items-center"
                                            >
                                                <span>
                                                    {index === arr.length - 1
                                                        ? `Your offer has been `
                                                        : `Your offer status has been changed to `}
                                                    <span className="fw-bold">{log.offerState}</span>
                                                </span>
                                                <small className="text-muted">{formatDate(log.logTime)}</small>
                                            </li>
                                        ))}
                                </ul>}
                            {offer && offer.stateLogs && offer.stateLogs.length === 0 && (
                                <p className="text-muted mb-2">No status log available.</p>)}
                        </div>
                    </div>
                </>
            )}

        </div>
    );
}
export default ViewOfferPage;